import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { CustomIcons } from '@shared/models/custom-icons';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, takeUntil, tap } from 'rxjs/operators';
import { ShowLayout } from '@shared/state/shared/shared.action';
import { CatalogView } from '@shared/models/shared-catalog.state';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { setLocationQueryParams } from '@utils/other-utils';
import { Location } from '@angular/common';
import { cloneDeep } from 'lodash';
import { ReleaseStatus } from '@shared/models/catalog.service';
import { TranslateService } from '@ngx-translate/core';
import { Locale } from '@shared/models/locale';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { CatalogItemsDataRouting } from '../../models/catalog-items-data-routing';
import { DataRouting } from '../../../catalog-data/models/data-routing';
import { CatalogItemsQueryParams } from '../../models/catalog-item-component';
import { ChangedCatalogItemsFilter } from '../../store/catalog-items.actions';
import { CatalogItemsState } from '../../store/catalog-items-state';
import { CatalogState } from '../../../../store/catalog/catalog.state';
import { ChangeCatalogFilter } from '../../../catalog-data/store/catalog-table.actions';
import { SharedState } from '@shared/state/shared/shared.state';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class HeaderComponent implements OnInit, OnDestroy, IDestroy {
  private _access: string;
  private _mapLink = {
    url: 'map',
    label: 'DATA.CATALOG_ITEMS_DATA.HEADER.MAP',
    link: `./${CatalogItemsDataRouting.Map}`,
    index: 1,
    params: {}
  };

  private _descriptionLink = {
    url: 'description',
    label: 'DATA.CATALOG_ITEMS_DATA.HEADER.DESCRIPTION',
    link: `./${CatalogItemsDataRouting.Description}`,
    index: 3,
    params: {}
  };

  private _navLinks = [
    {
      label: 'DATA.CATALOG_ITEMS_DATA.HEADER.TABLE',
      url: '',
      link: `./${CatalogItemsDataRouting.CatalogItems}`,
      index: 0,
      params: {}
    },
    {
      url: 'passport',
      label: 'DATA.CATALOG_ITEMS_DATA.HEADER.PASSPORT',
      link: `./${CatalogItemsDataRouting.Passport}`,
      index: 2,
      params: {}
    }
  ];
  public isMobileView = false;
  public catalogId: string;
  public componentDestroy: () => Subject<any>;
  public iconClass = 'svg18';
  public filter$: Observable<string>;
  public icons = CustomIcons;
  public catalogHeightCatalogName = 72;
  public mobilePaneHide = true;
  public langChange$: Observable<LangChangeEvent>;
  public isDynamic = false;
  public activeLink: string;
  public isNotPublicCatalog = false;
  @Output() public readonly headerHeight = new EventEmitter<boolean>();
  public navLinks: {
    label: string;
    url: string;
    link: string;
    index: number;
    params: Params;
  }[] = [];
  public selectCatalog$: Observable<CatalogView>;
  public newObjectPage = `/opendata/${DataRouting.Appeals}`;
  public newObjectPageParams: Params;
  public downloadCatalogId: number;
  public downloadReleaseDate: string;
  public arrowShow = false;
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 1530px)', '(max-width: 1024px)', '(max-width: 800px)', '(max-width: 442px)']).pipe(
    map(state => {
      this.iconClass = 'svg24';
      this.isMobileView = false;
      this.arrowShow = false;
      if (state.breakpoints['(max-width: 442px)']) {
        this.isMobileView = true;
        return 150;
      }
      if (state.breakpoints['(max-width: 800px)']) {
        this.isMobileView = true;
        return 200;
      }
      if (state.breakpoints['(max-width: 1024px)']) {
        this.isMobileView = false;
        this.arrowShow = true;
        return 250;
      }

      if (state.breakpoints['(max-width: 1530px)']) {
        this.setCatalogNameLength(515);
        return 515;
      }
      this.setCatalogNameLength(626);
      return 626;
    })
  );
  @Select(SharedState.getShowHeader) public showHeader$: Observable<boolean | null>;
  // @ts-expect-error-next-line
  public hideHeader$ = this.showHeader$.pipe(map((show) => !show));
  public isRuLocale = true;

  constructor(
    private location: Location,
    private store: Store,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private translateService: TranslateService,
    private route: ActivatedRoute
  ) {}

  getBackwardButtonWidth(breakPointValue: number): number {
    return breakPointValue ? 625 : 300;
  }

  getHeaderTitle(selectedSet?: CatalogView): string {
    if (!this.mobilePaneHide || !selectedSet) {
      return '';
    }
    return this.isRuLocale ? selectedSet.catalogName : selectedSet.catalogNameEn;
  }

  private setCatalogNameLength(maxWidth: number, version: 'pc' | 'ipad' | 'mobile' = 'pc'): void {
    const catalogName = this.store.selectSnapshot(SharedCatalogState.selectCatalog)?.catalogName;
    if (catalogName) {
      if ((catalogName.length * 8.5) / maxWidth > 2) {
        // eslint-disable-next-line default-case
        switch (version) {
          case 'pc': {
            this.catalogHeightCatalogName = 70;
            break;
          }
          case 'ipad': {
            this.catalogHeightCatalogName = 121;
            break;
          }
          case 'mobile': {
            this.catalogHeightCatalogName = 121;
            break;
          }
        }

      } else if ((catalogName.length * 8.5) / maxWidth > 1) {
        // eslint-disable-next-line default-case
        switch (version) {
          case 'pc': {
            this.catalogHeightCatalogName = 72;
            break;
          }
          case 'ipad': {
            this.catalogHeightCatalogName = 101;
            break;
          }
          case 'mobile': {
            this.catalogHeightCatalogName = 101;
            break;
          }
        }
      } else {
        // eslint-disable-next-line default-case
        switch (version) {
          case 'pc': {
            this.catalogHeightCatalogName = 72;
            break;
          }
          case 'ipad': {
            this.catalogHeightCatalogName = 81;
            break;
          }
          case 'mobile': {
            this.catalogHeightCatalogName = 81;
            break;
          }
        }
      }
    }
  }

  private setActiveLink(url: string): void {
    const selectCatalogInfo = this.store.selectSnapshot(SharedCatalogState.selectCatalogInfo);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const sefUrl = selectCatalogInfo?.details_opod?.sefUrl;
    const urlPath = url.split('/');
    const pathName = urlPath[urlPath.length - 1];
    if (pathName) {
      const currentLink = pathName.split('?')[0];
      if (!isNaN(+currentLink)) {
        this.activeLink = '';
      } else {
        this.activeLink = currentLink;
      }
      if (currentLink === sefUrl) {
        this.activeLink = '';
      }
    }
  }

  private setText(lang: Locale): void {
    if (lang === Locale.RU_RU) {
      this.isRuLocale = true;
      this.navLinks = [];
      this.navLinks.push(...this._navLinks);
      const selectCatalogInfo = this.store.selectSnapshot(SharedCatalogState.selectCatalogInfo);

      if (selectCatalogInfo?.description) {
        this.navLinks.push(this._descriptionLink);
      }

      const catalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
      if (catalog?.hasGeo && !this.navLinks.some(x => x.url === this._mapLink.url)) {
        this.navLinks.splice(1, 0, this._mapLink);
      }
    } else {
      this.isRuLocale = false;
      this.navLinks = [
        {
          url: 'passport',
          label: 'DATA.CATALOG_ITEMS_DATA.HEADER.PASSPORT',
          link: `./${CatalogItemsDataRouting.Passport}`,
          index: 2,
          params: {}
        }
      ];

      this.router.navigate([this.navLinks[0].link], { queryParamsHandling: 'preserve', relativeTo: this.route.parent });
    }
  }

  public ngOnInit(): void {
    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );
    const params = this.activeRoute.snapshot.queryParams as CatalogItemsQueryParams;
    if (params.access) {
      this._access = params.access;
    }
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);

    const catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease);
    if (catalogVersion && catalogVersion?.versionReleaseInfo) {
      this.isNotPublicCatalog = catalogVersion.versionReleaseInfo.releaseStatus === ReleaseStatus.New;
      this.downloadCatalogId = catalogVersion.versionReleaseInfo.catalogId;
      this.downloadReleaseDate = catalogVersion.versionReleaseInfo.releaseDate;
    } else if (selectCatalog.isDynamic) {
      this.downloadCatalogId = selectCatalog.id;
    }

    this.router.events.pipe(takeUntil(this.componentDestroy())).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.setActiveLink(event.url);
        const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
        if ((this.activeLink === '' || this.activeLink === 'map') && selectCatalog.isDynamic) {
          setLocationQueryParams(
            {
              access: this._access ?? null,
              isDynamic: true
            },
            this.router,
            this.location
          );
        }
        const catalogId = this.activeRoute.parent?.snapshot.paramMap.get('id');
        const params = this.activeRoute.snapshot.queryParams;
        if (catalogId && !params?.version && !params?.release && !selectCatalog.isDynamic) {
          let catalogVersion = this.store.selectSnapshot(CatalogState.currentCatalogVersionRelease)?.versionReleaseInfo;

          if (!catalogVersion) {
            const catalogVersions = this.store.selectSnapshot(CatalogState.getCatalogVersionsInfo).call(this, selectCatalog.datasetId);
            if (catalogVersions && catalogVersions.length > 0) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line array-callback-return,consistent-return
              catalogVersion = cloneDeep(catalogVersions)?.sort((x1, x2) => {
                if (x1.versionNum < x2.versionNum) return 1;
                if (x1.versionNum > x2.versionNum) return -1;

                if (x1.releaseNum < x2.releaseNum) return 1;
                if (x1.releaseNum > x2.releaseNum) return -1;
              })[0];
            }
          }

          if (catalogVersion) {
            setLocationQueryParams(
              {
                access: this._access ?? null,
                version: catalogVersion.versionNum,
                release: catalogVersion.releaseNum
              },
              this.router,
              this.location
            );
          }
        }

        if (this._access) {
          setLocationQueryParams(
            {
              access: this._access
            },
            this.router,
            this.location
          );
        }
      }
    });
    const datasetId = this.activeRoute.parent?.snapshot.paramMap.get('id');
    if (datasetId) {
      this.newObjectPageParams = {
        catalogGlobalId: selectCatalog.id
      };
      this.filter$ = this.store.select(CatalogItemsState.getCatalogItemCriteriaByDatasetId).pipe(
        map(filterFn => filterFn(+datasetId)),
        tap(res => {
          if (res) {
            this._mapLink.params = {
              filter: res
            };
          } else {
            this._mapLink.params = {};
          }

          this.setActiveLink(this.router.url);
        })
      );
    }

    if (params?.filter) {
      this.store.dispatch(new ChangedCatalogItemsFilter(params.filter, false));
    }

    if (params?.isDynamic && params.isDynamic === 'true') {
      this.isDynamic = true;
    }
    //   const selectCatalogInfo = this.store.selectSnapshot(SharedCatalogState.selectCatalogInfo);

    /*  if (selectCatalogInfo?.description && this.isRuLocale) {
      this.navLinks.push(this._descriptionLink);
    } */
    this.selectCatalog$ = this.store.select(SharedCatalogState.selectCatalog).pipe(
      tap(catalog => {
        if (catalog) {
          this.catalogId = catalog.id.toString();
          if (catalog.hasGeo && !this.navLinks.some(x => x.url === this._mapLink.url) && this.isRuLocale) {
            this.navLinks.splice(1, 0, this._mapLink);
          }
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.store.dispatch(new ShowLayout(true));
  }

  public setCategoryFilter(selectedSet: CatalogView): void {
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          openCategoryIds: [selectedSet.categoryId]
        },
        true
      )
    );
  }

  public stopPropagation($event: MouseEvent): void {
    $event.stopPropagation();
  }

  public showHideMobileHeader(): void {
    this.mobilePaneHide = !this.mobilePaneHide;
    this.headerHeight.next(!this.mobilePaneHide);
  }
}
